import React, { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { ReportView } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { fetcher } from '../../../services/fetcher';
import type { CrewStatisticsResponse, CrewStatsNrRow } from '../types';
import { StatsNotRecommendedReport } from './StatsNotRecommendedReport';
import {
  statsNotRecommendedBackPath,
  statsNotRecommendedListApiUrl,
} from './statsNotRecommendedPrintMeta';

const StatsNotRecommendedReportPage: React.FC = () => {
  const { colors } = useTheme();
  const [searchParams] = useSearchParams();

  const listUrl = useMemo(() => statsNotRecommendedListApiUrl(searchParams), [searchParams]);
  const { data, error, isLoading } = useSWR<CrewStatisticsResponse>(listUrl, fetcher, {
    revalidateOnFocus: false,
  });

  const vesselId = searchParams.get('vesselId')?.trim() ?? '';

  const vesselLabel = useMemo(() => {
    if (data?.vessel?.vesselName) return data.vessel.vesselName;
    if (!vesselId) return 'All vessels';
    return `Vessel #${vesselId}`;
  }, [data?.vessel?.vesselName, vesselId]);

  const rows = useMemo(
    () => (data?.lists?.notRecommended ?? []) as CrewStatsNrRow[],
    [data?.lists?.notRecommended],
  );

  const backTo = statsNotRecommendedBackPath();

  if (isLoading) {
    return (
      <div className={cn('flex min-h-[40vh] items-center justify-center text-sm', colors.textMuted)}>
        Loading not-recommended crew…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        className={cn(
          'flex min-h-[40vh] flex-col items-center justify-center gap-3 px-4',
          colors.background,
        )}
      >
        <p className={cn('text-sm', colors.textMuted)}>Could not load not-recommended statistics.</p>
        <Link to={backTo} className={cn('text-sm font-medium hover:underline', colors.primaryFg)}>
          ← Back to Not recommended
        </Link>
      </div>
    );
  }

  return (
    <ReportView
      backTo={backTo}
      backLabel="Back to Not recommended"
      title="Not recommended"
      subtitle={`${vesselLabel} · ${rows.length} crew`}
      printPageSize="A4 landscape"
      printMargin="0"
    >
      <StatsNotRecommendedReport rows={rows} vesselLabel={vesselLabel} />
    </ReportView>
  );
};

export default StatsNotRecommendedReportPage;
